import type { DefaultValues } from 'react-hook-form'
import type { Property } from '@/types'
import { CURRENCIES, DEFAULT_CURRENCY } from '@/types'
import type { AddPropertyFormValues } from './schema'
import { DEFAULT_VALUES } from './schema'

/**
 * Inverse of `buildPropertyPayload` in `./index.tsx` — rebuilds wizard form
 * values from a stored property so a draft can be reopened where it was left.
 *
 * The two must be kept in step: a field added to the payload but not here is
 * silently dropped the moment the draft is reopened and saved again, with no
 * error anywhere. There is no test covering this yet — app/web has no test
 * runner — so the pairing is review-enforced.
 */

const num = (v: string | number | null | undefined): number | undefined => {
	if (v === undefined || v === null || v === '') return undefined
	const n = Number(v)
	return Number.isFinite(n) ? n : undefined
}

/** Inverse of `elevatorCount`: 0 → 'NONE', 1..4 → '1'..'4'. */
const elevatorLevel = (v: number | undefined | null): AddPropertyFormValues['passengerElevators'] => {
	if (v === undefined || v === null) return undefined
	if (v === 0) return 'NONE'
	return v >= 1 && v <= 4 ? (String(v) as '1' | '2' | '3' | '4') : undefined
}

/**
 * gRPC enums reach the client either bare ('APARTMENT') or prefixed
 * ('PROPERTY_TYPE_APARTMENT') depending on serialization — the same mismatch
 * `lib/external-listing-status.ts` normalizes for listings. Selects compare
 * against the bare form, so strip the prefix before it reaches the form.
 */
const bare = <T extends string>(v: string | undefined | null, known: readonly T[]): T | undefined => {
	if (!v) return undefined
	if ((known as readonly string[]).includes(v)) return v as T
	const suffix = known.find((k) => v.endsWith(`_${k}`))
	return suffix
}

const UTILITY_TYPES = ['ELECTRICITY', 'WATER', 'HEATING', 'WASTE'] as const

const PROPERTY_TYPES  = ['APARTMENT', 'HOUSE', 'ROOM', 'OFFICE', 'LAND', 'RETAIL', 'WAREHOUSE', 'GARAGE', 'COWORKING'] as const
const OWNER_TYPES     = ['OWNER', 'AGENT'] as const
const HOUSING_TYPES   = ['APARTMENT', 'APARTMENTS'] as const
const ROOM_LOCATIONS  = ['APARTMENT', 'HOSTEL', 'HOTEL'] as const
const ROOM_HOUSING    = ['ROOM', 'BED'] as const
const HOUSE_KINDS     = ['HOUSE', 'DACHA', 'COTTAGE', 'TOWNHOUSE'] as const
// 'SEVERAL' and 'OTHER' are retired: still stored on legacy rows and still
// translated for display, but no longer offered by the step-2 selects. Leaving
// them out means a legacy draft reopens with the field visibly empty and
// required, instead of silently round-tripping a value the user cannot see.
const BATHROOM_TYPES  = ['COMBINED', 'SEPARATE'] as const
const RENOVATIONS     = ['NONE', 'COSMETIC', 'EURO', 'DESIGNER'] as const
const BUILDING_TYPES  = ['BRICK', 'PANEL', 'BLOCK', 'MONOLITH', 'MONOLITH_BRICK', 'WOOD'] as const
const PERIODICITIES   = ['MONTHLY', 'WEEKLY', 'QUARTERLY', 'YEARLY'] as const
const CALCULATIONS    = ['BY_METER', 'PROPORTIONAL', 'FIXED_PER_SQM', 'FIXED_AMOUNT', 'COEFFICIENT', 'INCLUDED'] as const
const PAID_BY         = ['TENANT', 'LANDLORD'] as const

export function propertyToFormValues(property: Property): DefaultValues<AddPropertyFormValues> {
	// Rent terms arrive nested, with flat accessors mirrored alongside them for
	// UI convenience. Prefer the nested shape; fall back to the flat one.
	const terms = property.rentTerms
	const rentAmount      = num(terms?.rentAmount      ?? property.rentAmount)
	const depositAmount   = num(terms?.depositAmount   ?? property.depositAmount)
	const penaltyRate     = num(terms?.penaltyRate     ?? property.penaltyRate)
	const graceDays       = num(terms?.penaltyGraceDays ?? property.penaltyGraceDays)

	const utilities = Object.fromEntries(
		UTILITY_TYPES.map((type) => {
			const stored = property.utilities?.find((u) => bare(u.type, UTILITY_TYPES) === type)
			// No stored row means "not billed" — the inverse of what
			// buildPropertyPayload does, which drops a utility without a
			// calculation. Seeding DEFAULT_VALUES here instead would switch a
			// deliberately disabled utility back on when the draft is reopened.
			if (!stored) return [type, { currency: DEFAULT_CURRENCY }]
			return [type, {
				calculation:      bare(stored.calculation, CALCULATIONS),
				paidBy:           bare(stored.paidBy, PAID_BY),
				hasSeparateMeter: stored.hasSeparateMeter ?? false,
				meterNumber:      stored.meterNumber ?? '',
				ratePerSqm:       num(stored.ratePerSqm),
				fixedAmount:      num(stored.fixedAmount),
				coefficient:      num(stored.coefficient),
				propertyArea:     num(stored.propertyArea),
				buildingArea:     num(stored.buildingArea),
				currency:         bare(stored.currency, CURRENCIES) ?? DEFAULT_CURRENCY,
			}]
		}),
	) as AddPropertyFormValues['utilities']

	const additionalServices = (property.additionalServices ?? []).map((s) => ({
		name:         s.name,
		pricePerUnit: num(s.pricePerUnit),
		currency:     bare(s.currency, CURRENCIES) ?? DEFAULT_CURRENCY,
		quantity:     num(s.quantity),
		unit:         s.unit,
		provisionType: s.provisionType ?? 'OWN',
		vatIncluded:   s.vatIncluded ?? true,
	}))

	return {
		// ── Step 1 ──────────────────────────────────────────────────────────
		type:            bare(property.type, PROPERTY_TYPES) ?? 'APARTMENT',
		city:            property.city ?? '',
		address:         property.address ?? '',
		apartmentNumber: property.apartmentNumber ?? '',
		ownerType:       bare(property.ownerType, OWNER_TYPES) ?? 'OWNER',
		phone:           property.phone ?? '',
		latitude:        num(property.latitude),
		longitude:       num(property.longitude),

		// ── Step 2 ──────────────────────────────────────────────────────────
		housingType:      bare(property.housingType, HOUSING_TYPES),
		roomLocation:     bare(property.roomLocation, ROOM_LOCATIONS),
		roomsInApartment: num(property.roomsInApartment),
		roomHousingType:  bare(property.roomHousingType, ROOM_HOUSING),
		bedCount:         num(property.bedCount),
		sleepingPlaces:   num(property.sleepingPlaces),
		eventsAllowed:    property.eventsAllowed,
		fireplace:        property.fireplace,

		houseKind:            bare(property.houseKind, HOUSE_KINDS),
		wallsType:            property.wallsType,
		sauna:                property.sauna,
		pool:                 property.pool,
		terrace:              property.terrace,
		plotArea:             num(property.plotArea),
		landCategory:         property.landCategory,
		bathroomLocations:    property.bathroomLocations ?? [],
		communications:       property.communications ?? [],
		transportAccess:      property.transportAccess ?? [],
		nearbyInfrastructure: property.nearbyInfrastructure ?? [],

		// Commercial premises, read back so an edit starts from what was saved
		// rather than from an empty form.
		onlineViewing:          property.onlineViewing,
		multipleFloors:         property.multipleFloors,
		officeLayouts:          property.officeLayouts ?? [],
		partialRent:            property.partialRent,
		ceilingHeight:          num(property.ceilingHeight),
		finishing:              property.finishing,
		powerCapacity:          num(property.powerCapacity),
		powerCapacityExpandable: property.powerCapacityExpandable,
		commercialBuildingType: property.commercialBuildingType,
		buildingClass:          property.buildingClass,
		commercialParking:      property.commercialParking,
		parkingFree:            property.parkingFree,
		parkingForTrucks:       property.parkingForTrucks,
		parkingSpaces:          property.parkingSpaces,
		entrance:               property.entrance,
		separateEntrance:       property.separateEntrance,
		commercialHeating:      property.commercialHeating,
		retailPlacement:        property.retailPlacement,
		additionalCategories:   property.additionalCategories ?? [],
		garageKind:             property.garageKind,
		garageType:             property.garageType,
		parkingSpaceType:       property.parkingSpaceType,
		security:               property.security,
		seatsCount: property.seatsCount,
		weekendsOpen: property.weekendsOpen,
		aroundTheClock: property.aroundTheClock,
		workplaceStatus: property.workplaceStatus,
		privateOfficeArea: num(property.privateOfficeArea),
		privateOfficeSeats: property.privateOfficeSeats,
		amenitiesInclusion: property.amenitiesInclusion,
		keyAmenities: property.keyAmenities ?? [],
		equipment: property.equipment ?? [],
		foodAndDrinks: property.foodAndDrinks ?? [],
		coworkingServices: property.coworkingServices ?? [],
		coworkingExtras: property.coworkingExtras ?? [],

		floor:        num(property.floor),
		rooms:        num(property.rooms),
		roomType:     property.roomType,
		balcony:      property.balcony,
		loggia:       property.loggia,
		wardrobe:     property.wardrobe,
		totalArea:    num(property.area),
		livingArea:   num(property.livingArea),
		kitchenSpace: num(property.kitchenSpace),

		// No bathroomCount here: the wizard dropped the field, so seeding it only
		// looks like the draft still carries one. DeepPartial swallows the extra
		// key silently rather than failing the build.
		bathroomType:  bare(property.bathroom, BATHROOM_TYPES),
		renovation:   bare(property.renovation, RENOVATIONS),
		furniture:    property.furniture  ?? [],
		appliances:   property.appliances ?? [],
		internet:     property.internet   ?? [],

		buildingType:       bare(property.buildingType, BUILDING_TYPES),
		concierge:          property.concierge,
		chute:              property.chute,
		gas:                property.gas,
		yearBuilt:          num(property.yearBuilt),
		totalFloors:        num(property.totalFloors),
		passengerElevators: elevatorLevel(property.passengerElevator),
		freightElevators:   elevatorLevel(property.freightElevator),
		yard:               property.yard    ?? [],
		parking:            property.parking ?? [],
		parkingType:        property.parkingType,

		maxTenants:      num(property.maxTenants),
		childrenAllowed: property.childrenAllowed,
		petsAllowed:     property.petsAllowed,
		smokingAllowed:  property.smokingAllowed,

		// ── Step 3 ──────────────────────────────────────────────────────────
		rentAmount,
		currency:         bare(terms?.rentCurrency ?? property.rentCurrency, CURRENCIES) ?? DEFAULT_CURRENCY,
		periodicity:      bare(terms?.rentPeriod ?? property.rentPeriod, PERIODICITIES) ?? 'MONTHLY',
		vatIncluded:      terms?.vatIncluded ?? true,
		depositAmount,
		depositCurrency:  bare(terms?.depositCurrency ?? property.depositCurrency, CURRENCIES) ?? DEFAULT_CURRENCY,
		penaltyPercent:   penaltyRate,
		penaltyGraceDays: graceDays,
		otherUtilitiesPayment: num(terms?.otherUtilitiesPayment),

		rentUnit:                  terms?.rentUnit,
		agentCommissionPercent:    num(terms?.agentCommissionPercent),
		leaseKind:                 terms?.leaseKind,
		rentHolidays:              terms?.rentHolidays,
		minLeaseMonths:            terms?.minLeaseMonths,
		utilitiesIncluded:         terms?.utilitiesIncluded,
		operatingExpensesIncluded: terms?.operatingExpensesIncluded,
		depositMonths:             terms?.depositMonths != null ? Number(terms.depositMonths) : undefined,

		// ── Steps 4-6 ───────────────────────────────────────────────────────
		utilities,
		// An empty list would render no rows at all, leaving nothing to fill in.
		additionalServices: additionalServices.length ? additionalServices : DEFAULT_VALUES.additionalServices,

		description: property.description ?? '',
		videoUrl:    property.videoUrl ?? '',
		images:      (property.images ?? []).map((i) => i.url),
	}
}
